import {AfterViewInit, Component, OnDestroy, OnInit, TemplateRef, ViewChild} from '@angular/core';
import {SnackService} from '../../../services/snack/snack.service';
import {AuthService} from '../../../services/auth/auth.service';
import {TranslateService} from '@ngx-translate/core';
import {RouteService} from '../../../services/route/route.service';
import {StorageService} from '../../../services/storage/storage.service';
import {RefundService} from '../../../services/refund/refund.service';
import {FiscalService} from '../../../services/fiscal/fiscal.service';
import {UtilService} from '../../../services/util/util.service';
import {CartService} from '../../../services/cart/cart.service';
import * as FileSaver from 'file-saver-es';
import {Subscription} from 'rxjs';
import {
  PaymentTypeEnum,
  RefundTicketActionEnum,
  RefundTickets,
  TotalInsurance,
  TotalSurcharge,
  Transaction
} from '../../../generated/sales-terminal-client';
import {finalize} from 'rxjs/operators';
import {Privileges} from '../../admin/privileges/privileges.constants';
import {DataTable, DataTableRowItemType} from '../../../components/data-table/data-table.interfaces';

@Component({
  selector: 'app-refund',
  templateUrl: './refund.component.html',
  styleUrls: ['./refund.component.scss']
})
export class RefundComponent implements OnInit, OnDestroy, AfterViewInit {
  public paymentTypes: PaymentTypeEnum[] = [];
  public currentPaymentType = PaymentTypeEnum.Cash;
  public actions = RefundTicketActionEnum;
  public isLoading: boolean;
  public isExchange = false;
  public modalIsOpened = false;
  public Privileges = Privileges;
  private _subscriptions: Subscription[] = [];
  public tableConfig: DataTable;

  @ViewChild('tableTicketId')
  tableTicketId: TemplateRef<any>;
  @ViewChild('tableMobileTicketId')
  tableMobileTicketId: TemplateRef<any>;
  @ViewChild('tableEvent')
  tableEvent: TemplateRef<any>;
  @ViewChild('tableDate')
  tableDate: TemplateRef<any>;
  @ViewChild('tableTicketType')
  tableTicketType: TemplateRef<any>;
  @ViewChild('tableTicketPrice')
  tableTicketPrice: TemplateRef<any>;

  constructor(public authService: AuthService,
              public refundService: RefundService,
              private snackService: SnackService,
              private translateService: TranslateService,
              private routeService: RouteService,
              private storageService: StorageService,
              private cartService: CartService,
              private fiscalService: FiscalService,
              private utilService: UtilService) {}

  public ngOnInit(): void {
    this.isExchange = this.storageService.get(RefundService.STORAGE_REFUND_EXCHANGE)?.toLowerCase() === 'true';
    this.refundService.updateCart(true);
    this.updateData();
  }
  public ngAfterViewInit(): void {
    this.setupTable();
  }
  public ngOnDestroy(): void {
    this.clearSubscriptions();
  }
  private clearSubscriptions(): void {
    this._subscriptions.forEach(s => s.unsubscribe());
    this._subscriptions = [];
  }

  public get canRefund(): boolean {
    return !!this.refundService.cart.events.length;
  }
  public get isMobile(): boolean {
    return this.utilService.isMobile;
  }

  private updateData(): void {
    this.isLoading = true;
    this.cartService.getPaymentTypes().pipe(
      finalize(() => this.isLoading = false)
    ).subscribe((paymentTypes) => this.paymentTypes = paymentTypes);
  }

  public get giftTicketIds(): number[] {
    return this.refundService.giftTicketIds;
  }
  public get nonRefundableSurcharges(): TotalSurcharge[] {
    return this.refundService.nonRefundableSurcharges;
  }
  public get refundableInsurances(): TotalInsurance[] {
    return this.refundService.refundableInsurances;
  }
  public get nonRefundableInsurances(): TotalInsurance[] {
    return this.refundService.nonRefundableInsurances;
  }

  public startRefund(): void {
    this.modalIsOpened = true;
  }
  public undoRefund() {
    this.modalIsOpened = false;
    this.snackService.showSuccess(this.translateService.instant('general.api_refund_undone'));
  }

  public confirmRefund(action: RefundTicketActionEnum = RefundTicketActionEnum.Sale) {
    const data: RefundTickets = { tickets: this.refundTicketIds, ticketAction: action, paymentType: this.currentPaymentType };
    if (this.authService.checkSalespointProperty('fiscal_procedures_by')) {
      this._subscriptions.push(this.utilService.fiscalOperationFinished.subscribe(() => {
        this.refundCallback(action);
        this.clearSubscriptions();
      }));
      this._subscriptions.push(this.utilService.fiscalOperationFailed.subscribe(() => {
        this.modalIsOpened = false;
        this.clearSubscriptions();
      }));
      this.fiscalService.refund(data);
    } else {
      this.refundService.startRefund(data).subscribe(refundTransaction => {
        this.refundCallback(action, refundTransaction);
      });
    }
  }
  private refundCallback(action: RefundTicketActionEnum, transaction?: Transaction) {
    this.refundService.updateCart(true, true);
    this.modalIsOpened = false;
    if (transaction && this.authService.checkSalespointProperty('fiscal_procedures_lt')) {
      this.refundService.refundFiscal(transaction.id).subscribe(blob => {
        const fileData = UtilService.getFileData('json_fiscal', 'json');
        if (fileData.firefox) {
          const file = new File([blob], fileData.filename, { type: fileData.mime });
          this.routeService.download(URL.createObjectURL(file), this.isExchange ? '/app/sales-terminal/cart' : this.routeService.prevUrl);
        } else {
          const file = new Blob([blob], {type: fileData.mime});
          FileSaver.saveAs(file, fileData.filename);
        }
        this.fiscalSuccess(action);
      });
    } else {
      this.fiscalSuccess(action);
    }
  }
  private fiscalSuccess(action: RefundTicketActionEnum): void {
    let message: string;
    switch (action) {
      case RefundTicketActionEnum.Sale:
        message = 'general.api_refund_back_to_sale_succeed'; break;
      case RefundTicketActionEnum.Cart:
        message = 'general.api_refund_exchange_succeed'; break;
      default:
        message = 'general.api_refund_succeed'; break;
    }
    this.cartService.retryCart(true);
    this.snackService.showSuccess(this.translateService.instant(message));
  }

  public cancelRefund(): void {
    this.removeTickets(this.refundTicketIds);
    this.routeService.back();
  }

  private get refundTicketIds(): number[] {
    let ticketIds: number[] = [];
    this.refundService.cart.events.forEach(event => {
      ticketIds = ticketIds.concat(event.legacyTickets.map(t => t.id));
    });
    return ticketIds;
  }

  public get hasGiftTickets(): boolean {
    return !!this.giftTicketIds.length;
  }
  public get displayActiveGiftTicketIds(): string {
    return this.giftTicketIds.join(', ');
  }

  public findGiftTicket(id: number): boolean {
    return this.giftTicketIds && this.giftTicketIds.indexOf(id) !== -1;
  }

  public removeTickets(ticketIds: number[]): void {
    const removableTicketIds: number[] = ticketIds.filter(ticketId => this.refundService.selectedTicketIds.includes(ticketId));
    this.refundService.deSelectedEvents = this.refundService.deSelectedEvents.filter(deSelectedEvent => {
      deSelectedEvent.legacyTickets = deSelectedEvent.legacyTickets.filter(ticket => !ticketIds.includes(ticket.id));
      return !!deSelectedEvent.legacyTickets?.length;
    });
    if (removableTicketIds.length) {
      this.isLoading = true;
      this.refundService.removeTickets({tickets: removableTicketIds}).pipe(
        finalize(() => this.isLoading = false)
      ).subscribe(_ => {
        this.refundService.updateCart(true);
      });
    } else {
      this.refundService.updateCart(true);
    }
  }

  private setupTable(): void {
    this.tableConfig = {
      header: {
        items: [
          {
            key: 'selected',
            value: false,
            format: 'general.api_cart_table_ticket_id',
            type: DataTableRowItemType.Checkbox,
            cssClass: 'min-width'
          },
          {
            key: 'ticket_id',
            format: 'general.api_cart_table_ticket_id'
          },
          {
            key: 'event',
            format: 'general.api_cart_table_event_title',
            children: [
              {
                key: 'venue',
                format: 'general.api_cart_table_event_venue',
                cssClass: 'subtitle'
              }
            ]
          },
          {
            key: 'date',
            format: 'general.api_cart_table_date'
          },
          {
            key: 'sector',
            format: 'general.api_cart_table_sector',
          },
          {
            key: 'row',
            format: 'general.api_cart_table_row',
          },
          {
            key: 'seat',
            format: 'general.api_cart_table_seat',
          },
          {
            key: 'ticketType',
            format: 'general.api_cart_table_type'
          },
          {
            key: 'ticketPrice',
            format: 'general.api_cart_table_price',
            cssClass: 'align-right'
          },
          {
            key: 'buttons',
            width: 1
          }
        ]
      },
      body: {
        items: [
          {
            key: 'selected',
            value: 'id',
            type: DataTableRowItemType.Checkbox,
            mobileHeaderItem: true
          },
          {
            key: 'ticket_id',
            type: DataTableRowItemType.Template,
            template: this.tableTicketId,
            templateMobile: this.tableMobileTicketId,
            mobileHeaderItem: true
          },
          {
            key: 'event',
            useParent: true,
            type: DataTableRowItemType.Template,
            template: this.tableEvent,
            templateMobile: this.tableEvent
          },
          {
            key: 'date',
            useParent: true,
            type: DataTableRowItemType.Template,
            template: this.tableDate,
            templateMobile: this.tableDate,
            cssClass: 'white-space-no-wrap'
          },
          {
            key: 'sector',
            format: 'sectorName',
            useModel: true,
          },
          {
            key: 'row',
            format: 'row',
            useModel: true,
          },
          {
            key: 'place',
            format: 'place',
            useModel: true,
          },
          {
            key: 'ticketType',
            type: DataTableRowItemType.Template,
            template: this.tableTicketType,
            templateMobile: this.tableTicketType
          },
          {
            key: 'ticketPrice',
            type: DataTableRowItemType.Template,
            template: this.tableTicketPrice,
            templateMobile: this.tableTicketPrice,
            cssClass: 'align-right'
          },
          {
            key: 'buttons',
            type: DataTableRowItemType.Button,
            buttons: [
              {
                action: (item: any) => {
                  this.removeTickets([item.id]);
                },
                disabled: this.modalIsOpened,
                icon: 'trash'
              }
            ],
            mobileHeaderItem: true
          }
        ]
      },
      customSelector: 'legacyTickets'
    };
  }
}
